// Size Chart Scroll Detection
class SizeChartScrollDetector {
  constructor() {
    this.init();
  }

  init() {
    this.attachScrollListeners();
  }

  attachScrollListeners() {
    const sizeChartTables = document.querySelectorAll('.size-chart__data-table');

    sizeChartTables.forEach(table => {
      this.setupScrollIndicator(table);
    });
  }

  setupScrollIndicator(table) {
    // Create scroll indicator element
    const indicator = document.createElement('div');
    indicator.className = 'size-chart__scroll-indicator';

    // Position indicator relative to the size chart wrapper
    const sizeChartWrapper = table.closest('.size-chart__wrapper') || table.parentElement;
    sizeChartWrapper.appendChild(indicator);

    const updateIndicatorPosition = () => {
      const tableRect = table.getBoundingClientRect();
      const wrapperRect = sizeChartWrapper.getBoundingClientRect();

      // Position indicator at the right edge of the visible table area
      indicator.style.right = `${wrapperRect.right - tableRect.right}px`;
    };

    const checkScroll = () => {
      const isScrollable = table.scrollWidth > table.clientWidth;

      // Find the last cell in the first row (header row)
      const firstRow = table.querySelector('tr');
      const lastCell = firstRow ? firstRow.lastElementChild : null;

      let isLastCellVisible = false;
      if (lastCell && isScrollable) {
        const cellRect = lastCell.getBoundingClientRect();
        const tableRect = table.getBoundingClientRect();

        // Get computed styles to account for padding
        const computedStyle = window.getComputedStyle(lastCell);
        const paddingRight = parseFloat(computedStyle.paddingRight) || 0;

        // Calculate the actual content area (excluding padding)
        const contentRight = cellRect.right - paddingRight;

        // Check if the content area (without padding) is fully visible
        isLastCellVisible = contentRight <= tableRect.right;

        // Debug logging
        console.log('Scroll check:', {
          isScrollable,
          isLastCellVisible,
          cellRight: cellRect.right,
          paddingRight,
          contentRight,
          tableRight: tableRect.right,
          scrollLeft: table.scrollLeft
        });
      }

      updateIndicatorPosition();

      if (isScrollable && !isLastCellVisible) {
        indicator.classList.add('size-chart__scroll-indicator--visible');
      } else {
        indicator.classList.remove('size-chart__scroll-indicator--visible');
      }
    };

    // Check on scroll
    table.addEventListener('scroll', checkScroll);

    // Check on resize
    window.addEventListener('resize', checkScroll);

    // Initial check
    checkScroll();
  }
}

// Initialize size chart scroll detection when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  new SizeChartScrollDetector();
});

// Re-initialize when drawer content changes (for dynamic content)
document.addEventListener('drawer:opened', () => {
  setTimeout(() => {
    new SizeChartScrollDetector();
  }, 100);
});

// Product Modal Class
class ProductModal extends ModalDialog {
  constructor() {
    super();
    this.gallery = null;
    this.thumbnails = null;
  }

  hide() {
    this.gallery.destroy(true, true);
    this.thumbnails.destroy(true, true);
    super.hide();
    window.pauseAllMedia();
  }

  show(opener) {
    super.show(opener);
    this.showActiveMedia();
    this.initSwiper();
  }

  initSwiper() {
    const thumbsCommonSettings = {
      watchSlidesProgress: true,
      spaceBetween: 5,
      navigation: {
        nextEl: '.swiper-button-next',
        prevEl: '.swiper-button-prev',
      }
    }

    const galleryCommonSettings = {
      mousewheel: true,
      freeMode: true,
      autoHeight: true,
      observer: true,
      observeParents: true,
    }

    //desktop
    if (window.innerWidth >= window.breakpoints.desktop.min) {
      this.thumbnails = new Swiper('#gallery-thumbnails', {
        ...thumbsCommonSettings,
        direction: "vertical",
        slidesPerView: 5,
      });

      this.gallery = new Swiper('#gallery-images', {
        ...galleryCommonSettings,
        direction: "vertical",
        slidesPerView: 'auto',
        thumbs: {
          swiper: this.thumbnails
        },
        scrollbar: {
          el: '.swiper-scrollbar-gallery',
          draggable: true,
          hide: false
        },
      });

    } else { //mobile
      this.thumbnails = new Swiper('#gallery-thumbnails', {
        ...thumbsCommonSettings,
        direction: "horizontal",
        slidesPerView: "auto",
      });

      this.gallery = new Swiper('#gallery-images', {
        ...galleryCommonSettings,
        direction: "horizontal",
        slidesPerView: 1,
        thumbs: {
          swiper: this.thumbnails
        },
        scrollbar: {
          el: '.swiper-scrollbar-gallery',
          draggable: true,
          hide: false
        },
      });
    }
  }

  showActiveMedia() {
    this.querySelectorAll(`[data-media-id]:not([data-media-id="${this.openedBy.getAttribute("data-media-id")}"])`).forEach((element) => {
      element.classList.remove('active');
    }
    )
    const activeMedia = this.querySelector(`[data-media-id="${this.openedBy.getAttribute("data-media-id")}"]`);
    activeMedia.classList.add('active');
    activeMedia.scrollIntoView();

    if (activeMedia.nodeName == 'DEFERRED-MEDIA' && activeMedia.querySelector('template')?.content?.querySelector('.js-youtube'))
      activeMedia.loadContent();
  }
}

// Register the ProductModal custom element
customElements.define('product-modal', ProductModal);

// Main Product Page Functionality
document.addEventListener('DOMContentLoaded', function () {
  // Initialize size chart drawer
  customElements.whenDefined('drawer-component').then(() => {
    initializeSizeChartDrawer();
    initializeStoreAvailabilityDrawer();
  });

  // Initialize thumbnail navigation
  initializeThumbnailNavigation();

  // Initialize product functionality
  initializeProductFunctionality();

  // Initialize sticky ATC z-index management
  initializeStickyAtcZIndex();

  // Initialize sticky mobile footer
});

// Size Chart Drawer Functionality
function initializeSizeChartDrawer() {
  const sizeChartDrawer = document.querySelector('#size-chart-component');

  if (sizeChartDrawer) {
    const triggers = document.querySelectorAll('.size-chart-trigger');

    triggers.forEach(function (trigger) {
      trigger.addEventListener('click', function (e) {
        const isFitsLink = trigger.getAttribute('href') === '#fits' || trigger.classList.contains('product__media-badge');
        if (isFitsLink) {
          e.preventDefault();
          sizeChartDrawer.open({
            activeElement: this
          });
          // After drawer opens, scroll to #fits inside drawer-wrapper
          setTimeout(() => {
            const fitsEl = sizeChartDrawer.querySelector('#fits');
            if (fitsEl) {
              fitsEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
          }, 250);
        } else {
          sizeChartDrawer.open({
            activeElement: this
          });
        }
      });
    });
  }
}

// Store Availability Drawer Functionality
function initializeStoreAvailabilityDrawer() {
  const storeAvailabilityDrawer = document.querySelector('#store-availability-component');

  if (storeAvailabilityDrawer) {
    // Handle links with href="#store_availability"
    const triggers = document.querySelectorAll('a[href="#store_availability"]');

    triggers.forEach(function (trigger) {
      trigger.addEventListener('click', function (e) {
        e.preventDefault();
        storeAvailabilityDrawer.open({
          activeElement: this
        });
      });
    });
  }
}

// Thumbnail Navigation Functionality
function initializeThumbnailNavigation() {
  const thumbnailsNav = document.querySelector('.product__thumbnails-nav');
  if (!thumbnailsNav) return;

  const productEl = thumbnailsNav.closest('.product');
  const scrollThumbToCenter = productEl?.getAttribute('data-thumb-scroll-to-center') === 'true';

  const thumbnailButtons = thumbnailsNav.querySelectorAll('.product__thumbnail-button');
  const mediaItems = document.querySelectorAll('.product__media-item');

  if (!mediaItems.length || !thumbnailButtons.length) return;

  const count = Math.min(thumbnailButtons.length, mediaItems.length);
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  let isProgrammaticScroll = false;
  let programmaticScrollTarget = null;
  let scrollAnimationFrame = null;
  let lastScrollY = window.scrollY;

  function updateActiveThumbnail() {
    if (isProgrammaticScroll && programmaticScrollTarget !== null) {
      thumbnailButtons.forEach((btn, i) => {
        if (i >= count) return;

        const isActive = i === programmaticScrollTarget;
        btn.classList.toggle('active', isActive);
        btn.setAttribute('aria-selected', isActive ? 'true' : 'false');
        btn.setAttribute('tabindex', isActive ? '0' : '-1');
      });
      return;
    }

    const viewportHeight = window.innerHeight;
    const viewportCenter = viewportHeight / 2;

    let activeIndex = 0;

    if (window.scrollY < 100) {
      activeIndex = 0;
    } else {
      let bestIndex = 0;
      let bestScore = -1;

      mediaItems.forEach((item, index) => {
        if (index >= count) return;

        const rect = item.getBoundingClientRect();
        const itemTop = rect.top;
        const itemBottom = rect.bottom;
        const itemHeight = rect.height;

        const visibleTop = Math.max(itemTop, 0);
        const visibleBottom = Math.min(itemBottom, viewportHeight);
        const visibleHeight = Math.max(0, visibleBottom - visibleTop);

        if (visibleHeight > itemHeight * 0.2) {
          const visibilityRatio = visibleHeight / itemHeight;
          const distanceFromCenter = Math.abs((itemTop + itemHeight / 2) - viewportCenter);
          const centerProximity = Math.max(0, 1 - (distanceFromCenter / viewportHeight));

          const score = visibilityRatio + (centerProximity * 0.8);

          if (score > bestScore) {
            bestScore = score;
            bestIndex = index;
          }
        }
      });

      activeIndex = bestIndex;
    }

    thumbnailButtons.forEach((btn, i) => {
      if (i >= count) return;

      const isActive = i === activeIndex;
      btn.classList.toggle('active', isActive);
      btn.setAttribute('aria-selected', isActive ? 'true' : 'false');
      btn.setAttribute('tabindex', isActive ? '0' : '-1');
    });
  }

  function throttledUpdateActiveThumbnail() {
    if (scrollAnimationFrame) return;

    scrollAnimationFrame = requestAnimationFrame(() => {
      const currentScrollY = window.scrollY;
      const scrollDifference = Math.abs(currentScrollY - lastScrollY);

      if (isProgrammaticScroll && programmaticScrollTarget !== null) {
        const targetMedia = mediaItems[programmaticScrollTarget];
        if (targetMedia) {
          const rect = targetMedia.getBoundingClientRect();
          const viewportCenter = window.innerHeight / 2;
          const isAtTarget = scrollThumbToCenter
            ? Math.abs(rect.top + rect.height / 2 - viewportCenter) < 100
            : Math.abs(rect.top) < 100;

          if (isAtTarget && scrollDifference < 5) {
            isProgrammaticScroll = false;
            programmaticScrollTarget = null;
          }
        }
      }

      lastScrollY = currentScrollY;
      updateActiveThumbnail();
      scrollAnimationFrame = null;
    });
  }

  function handleThumbnailClick(index) {
    if (index >= count) return;

    thumbnailButtons.forEach((btn, i) => {
      if (i >= count) return;

      const isActive = i === index;
      btn.classList.toggle('active', isActive);
      btn.setAttribute('aria-selected', isActive ? 'true' : 'false');
      btn.setAttribute('tabindex', isActive ? '0' : '-1');
    });

    const targetMedia = mediaItems[index];
    if (targetMedia) {
      isProgrammaticScroll = true;
      programmaticScrollTarget = index;
      lastScrollY = window.scrollY;

      targetMedia.scrollIntoView({
        behavior: prefersReducedMotion ? 'auto' : 'smooth',
        block: scrollThumbToCenter ? 'center' : 'start'
      });
    }
  }

  thumbnailButtons.forEach((button, index) => {
    if (index >= count) return;

    button.addEventListener('click', () => handleThumbnailClick(index));
  });

  window.addEventListener('scroll', throttledUpdateActiveThumbnail, { passive: true });
  window.addEventListener('resize', throttledUpdateActiveThumbnail, { passive: true });
  window.addEventListener('load', throttledUpdateActiveThumbnail);

  updateActiveThumbnail();

  // On obuwie (scrollThumbToCenter): scroll first image to bottom on initial page load
  if (scrollThumbToCenter && mediaItems[0]) {
    const scrollFirstToBottom = () => {
      mediaItems[0].scrollIntoView({
        behavior: 'auto',
        block: 'end'
      });
    };
    if (document.readyState === 'complete') {
      requestAnimationFrame(scrollFirstToBottom);
    } else {
      window.addEventListener('load', scrollFirstToBottom, { once: true });
    }
  }
}

// Product Functionality (Variants, Availability, etc.)
function initializeProductFunctionality() {
  // Get product data from the JSON script tag
  const productDataScript = document.querySelector('[id^="product-data-"]');
  if (!productDataScript) return;

  const productData = JSON.parse(productDataScript.textContent);
  const { product, initialVariant, variants, variantOptions, isGiftCard, sectionId } = productData;

  // Log initial inventory quantity
  console.log('Product Page Load - Initial Variant:', {
    variantId: initialVariant.id,
    variantTitle: initialVariant.title,
    inventoryQuantity: initialVariant.inventory_quantity,
    inventoryPolicy: initialVariant.inventory_policy,
    available: initialVariant.available,
    initialVariant: initialVariant
  });

  const sizeCheck = document.querySelector("[data-hide-sizes]")?.getAttribute("data-hide-sizes");
  const variantRadios = document.querySelectorAll("variant-radios input[type='radio']");
  const initialSelection = document.querySelector(`variant-radios fieldset[data-option-name='${variantOptions[1]}'] input:checked`);

  let swiperGallery = null;

  initAvailability();

  if (initialSelection) {
    updateAvailability(variantOptions[1], initialSelection.value, initialSelection.getAttribute('data-option-number'));
  }

  document.querySelector("variant-radios")?.addEventListener("change", (event) => {
    if (!event) return;
    updateAvailability(event.target.getAttribute("name"), event.target.value, event.target.getAttribute("data-option-number"));
  });

  function initAvailability() {
    let allOptions;
    let availableOption1 = [];
    let availableOption2 = [];

    variants.forEach(variant => {
      const option1 = variant.option1;
      const option2 = variant.option2;
      if (variant.available) {
        if (availableOption1.indexOf(option1) === -1) {
          availableOption1.push(option1);
        }
        if (availableOption2.indexOf(option2) === -1) {
          availableOption2.push(option2);
        }
      }
    });

    allOptions = availableOption1.concat(availableOption2);

    if (!isGiftCard) {
      variantRadios.forEach(radio => {
        const value = radio.value;

        if (allOptions.indexOf(value) !== -1) {
          radio.disabled = false;
        } else {
          radio.disabled = true;
          const sizeCheckValue = "-" + value + "-";
          if (sizeCheck && sizeCheck !== "--" && sizeCheck.indexOf(sizeCheckValue) > -1) {
            const radioId = radio.id;
            const label = document.querySelector(`variant-radios label[for='${radioId}']`);
            if (label) {
              label.classList.add('hidden');
            }
          }
        }
      });
    }
  }

  function updateAvailability(optionName, value, optionNumber) {
    if (variantOptions.length === 1) return;

    let oppositeOption = null;
    let oppositeOptionIndex = null;
    let oppositeOptionNumber = null;

    if (optionName === variantOptions[0]) {
      oppositeOption = variantOptions[1];
      oppositeOptionIndex = 1;
      oppositeOptionNumber = 2;
    } else if (optionName === variantOptions[1]) {
      oppositeOption = variantOptions[0];
      oppositeOptionIndex = 0;
      oppositeOptionNumber = 1;
    }

    const availableVariants = variants.filter(v => (v['option' + optionNumber] === value && v.available)).map(obj => obj['option' + oppositeOptionNumber]);

    const oppositeInputs = document.querySelectorAll(`variant-radios fieldset[data-option-name='${oppositeOption}'] input`);

    oppositeInputs.forEach(v => {
      if (availableVariants.indexOf(v.value) > -1) {
        v.disabled = false;
      } else {
        v.disabled = true;
        v.checked = false;
      }
    });

    const selectedOption1 = document.querySelector(`variant-radios fieldset[data-option-name='${optionName}'] input:checked`);
    const selectedOption2 = document.querySelector(`variant-radios fieldset[data-option-name='${oppositeOption}'] input:checked`);

    const submitButtons = document.querySelectorAll(".product-form__submit");

    submitButtons.forEach(submitButton => {
      if (!selectedOption1 || !selectedOption2) {
        submitButton.disabled = true;
      } else {
        submitButton.disabled = false;
      }
    });
  }

  function initSwiper() {
    swiperGallery = new Swiper('#gallery-images', {
      slidesPerView: 1,
      direction: "vertical",
      mousewheel: true,
      freeMode: true
    });
  }

  function destroySwiper() {
    if (swiperGallery) {
      swiperGallery.destroy();
      swiperGallery = null;
    }
  }

  // Listen for variant changes to update quantity input max
  document.addEventListener('variant:change', (event) => {
    if (event.detail && event.detail.variant) {
      updateQuantityInputMax(event.detail.variant);
    }
  });

  // Also listen for variant-selects and variant-radios change events
  document.querySelector('variant-selects')?.addEventListener('change', () => {
    setTimeout(() => {
      const variantSelects = document.querySelector('variant-selects');
      if (variantSelects && variantSelects.currentVariant) {
        updateQuantityInputMax(variantSelects.currentVariant);
      }
    }, 100);
  });

  document.querySelector('variant-radios')?.addEventListener('change', () => {
    setTimeout(() => {
      const variantRadios = document.querySelector('variant-radios');
      if (variantRadios && variantRadios.currentVariant) {
        updateQuantityInputMax(variantRadios.currentVariant);
      }
    }, 100);
  });

  function updateQuantityInputMax(variant) {
    const quantityInput = document.querySelector('input[name="quantity"]');
    if (!quantityInput || !variant) return;

    const inventoryQuantity = variant.inventory_quantity;
    const inventoryPolicy = variant.inventory_policy;

    console.log('Product.js - updateQuantityInputMax:', {
      variantId: variant.id,
      variantTitle: variant.title,
      inventoryQuantity: inventoryQuantity,
      inventoryPolicy: inventoryPolicy,
      variantObject: variant
    });

    // Set max quantity based on inventory policy
    let maxQuantity = 999; // Default for unlimited or continue policy
    if (inventoryPolicy !== 'continue' && inventoryQuantity !== null && !isNaN(inventoryQuantity)) {
      maxQuantity = inventoryQuantity;
    }

    console.log('Setting max quantity:', {
      maxQuantity: maxQuantity,
      inventoryPolicy: inventoryPolicy,
      inventoryQuantity: inventoryQuantity
    });

    quantityInput.setAttribute('max', maxQuantity);
    quantityInput.setAttribute('data-inventory-quantity', inventoryQuantity || '');
    quantityInput.setAttribute('data-inventory-policy', inventoryPolicy || '');

    // Update quantity-input wrapper if it exists
    const quantityInputWrapper = quantityInput.closest('quantity-input');
    if (quantityInputWrapper) {
      quantityInputWrapper.setAttribute('data-variant-id', variant.id);
      quantityInputWrapper.setAttribute('data-inventory-quantity', inventoryQuantity || '');
      quantityInputWrapper.setAttribute('data-inventory-policy', inventoryPolicy || '');
    }

    // If current quantity exceeds max, adjust it
    const currentQuantity = parseInt(quantityInput.value, 10);
    if (currentQuantity > maxQuantity) {
      console.log('Adjusting quantity from', currentQuantity, 'to', maxQuantity);
      quantityInput.value = maxQuantity;
    }
  }

  // Initialize custom dropdown functionality
  customElements.whenDefined('custom-dropdown').then(() => {
    const addToCartButtons = document.querySelectorAll('.product-form__submit');
    const dropdowns = document.querySelectorAll('.custom-variant-dropdown');

    if (!addToCartButtons.length || dropdowns.length === 0) return;

    // Track which dropdowns have selections
    const dropdownSelections = new Map();

    // Initialize dropdown selections based on current state
    dropdowns.forEach(dropdown => {
      const dropdownId = dropdown.id;
      const optionIndex = dropdownId.split('-').pop();

      // Check if this dropdown has a selected option
      const selectedOption = dropdown.querySelector('.custom-dropdown__option[aria-selected="true"]');
      const isSelected = selectedOption !== null;

      dropdownSelections.set(dropdownId, isSelected);
    });

    // Function to update button state
    function updateButtonState() {
      const allSelected = Array.from(dropdownSelections.values()).every(selected => selected);

      addToCartButtons.forEach(addToCartButton => {
        if (allSelected) {
          // All dropdowns have selections - enable submit
          addToCartButton.textContent = window.theme?.strings?.products?.product?.add_to_cart || 'Add to cart';
          addToCartButton.type = 'submit';
          addToCartButton.disabled = false;
        } else {
          // Some dropdowns unselected - make it a button that opens dropdown
          addToCartButton.type = 'button';
          addToCartButton.disabled = false;

          // Find the first unselected dropdown and show its label
          for (const [dropdownId, selected] of dropdownSelections) {
            if (!selected) {
              const dropdown = document.getElementById(dropdownId);
              if (dropdown) {
                const label = dropdown.querySelector('.custom-dropdown__label');
                if (label) {
                  addToCartButton.textContent = label.textContent;
                }
              }
              break;
            }
          }
        }
      });
    }

    // Handle dropdown changes
    dropdowns.forEach(dropdown => {
      dropdown.addEventListener('change', (e) => {
        const selectedValue = e.detail.value;

        // Update the hidden native select
        const dropdownId = dropdown.id;
        const optionIndex = dropdownId.split('-').pop();
        const nativeSelect = document.querySelector(`#Option-${sectionId}-${optionIndex}-native`);

        if (nativeSelect) {
          nativeSelect.value = selectedValue;
        }

        // Mark this dropdown as selected
        dropdownSelections.set(dropdownId, true);

        // Update button state
        updateButtonState();

        // Only trigger variant change if ALL dropdowns are selected
        const allSelected = Array.from(dropdownSelections.values()).every(selected => selected);
        if (allSelected) {
          // Now dispatch change events for all native selects
          dropdowns.forEach(dropdown => {
            const dropdownId = dropdown.id;
            const optionIndex = dropdownId.split('-').pop();
            const nativeSelect = document.querySelector(`#Option-${sectionId}-${optionIndex}-native`);
            if (nativeSelect) {
              nativeSelect.dispatchEvent(new Event('change', { bubbles: true }));
            }
          });

          // Trigger variant change on the variant-selects component
          // const variantSelects = dropdown.closest('variant-selects');
          // if (variantSelects) {
          //   variantSelects.dispatchEvent(new Event('change', { bubbles: true }));
          // }
        }
      });
    });

    // Handle ATC button click
    addToCartButtons.forEach(addToCartButton => {
      addToCartButton.addEventListener('click', (e) => {
        const allSelected = Array.from(dropdownSelections.values()).every(selected => selected);

        if (!allSelected) {
          e.preventDefault();
          e.stopPropagation();

          // Find and open the first unselected dropdown
          for (const [dropdownId, selected] of dropdownSelections) {
            if (!selected) {
              const dropdown = document.getElementById(dropdownId);
              if (dropdown) {
                dropdown.focus();
                break;
              }
            }
          }
        }
      });
    });

    // Initialize button state
    updateButtonState();
  });
}

// Sticky ATC Z-Index Management
function initializeStickyAtcZIndex() {
  const stickyAtc = document.querySelector('.product__sticky-atc');
  if (!stickyAtc) return;

  let isSticking = false;
  let scrollAnimationFrame = null;

  function updateZIndex() {
    if (scrollAnimationFrame) return;

    scrollAnimationFrame = requestAnimationFrame(() => {
      const rect = stickyAtc.getBoundingClientRect();
      const viewportHeight = window.innerHeight;

      // Check if element is sticking (at bottom of viewport)
      const isAtBottom = rect.bottom >= viewportHeight - 10; // 10px tolerance

      if (isAtBottom && !isSticking) {
        // Element is sticking - set high z-index
        stickyAtc.style.zIndex = '4';
        isSticking = true;
      } else if (!isAtBottom && isSticking) {
        // Element is not sticking - set low z-index
        stickyAtc.style.zIndex = '2';
        isSticking = false;
      }

      scrollAnimationFrame = null;
    });
  }

  // Add scroll and resize listeners
  window.addEventListener('scroll', updateZIndex, { passive: true });
  window.addEventListener('resize', updateZIndex, { passive: true });

  // Initial check
  updateZIndex();
}
